import { AISearchHeader, Box, Grid, newToast, pn, Stack, useElementBreakpoints } from "@compass/core";
import { CustomFilters, Select } from "components";
import { CustomEntityOption } from "components/common/CustomFilters/CustomFilter";
import {
  AiPrompt,
  ControlGroupStudentFilterResource,
  ControlGroupStudentFilter as ControlGroupStudentFilterModel,
  CustomFilter,
  CustomFilterEntity,
  LkRoleId,
  StudentFilterResource,
} from "generated/openApi";
import produce from "immer";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import { RootState } from "redux/store";
import ApiService from "services/apiService";

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, "0");
  const day = `${today.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const ControlGroupStudentFilter = (props: {
  filter: ControlGroupStudentFilterModel;
  setFilter: (filter: ControlGroupStudentFilterModel) => void;
  customFilters?: CustomFilter[] | undefined;
  setCustomFilters?: (filter: CustomFilter[] | undefined) => void;
}) => {
  const { breakpointsRef, largeFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<ControlGroupStudentFilterResource>({} as ControlGroupStudentFilterResource);
  const [studentResource, setStudentResource] = useState<StudentFilterResource>({} as StudentFilterResource);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>();
  const [aiSearchPrompt, setAiSearchPrompt] = useState("");
  const [completedAiSearchPrompt, setCompletedAiSearchPrompt] = useState("");
  const properties = resource.properties || [];
  const dispatch = useDispatch();
  const appUserRoleId: number | undefined = useSelector((state: RootState) => state.app.roleId);
  const appUserId: number | undefined = useSelector((state: RootState) => state.app.userId);
  const aiSearchContext = "StudentFilters";
  const entityOptions: CustomEntityOption[] = [
    { EntityType: CustomFilterEntity.Student, EntityName: "Demographics" },
    { EntityType: CustomFilterEntity.StudentEnrollment, EntityName: "Enrollments" },
  ];

  useEffect(() => {
    ApiService.controlGroupStudents.getFilterResource().then((response) => {
      setResource(response);
    });

    ApiService.students.getFilterResource().then((response) => {
      setStudentResource(response);
    });
  }, []);

  const submitAiFilter = (prompt: string) => {
    const request: AiPrompt & { todayDate?: string } = {
      prompt,
      customFilterEntities: [CustomFilterEntity.Student, CustomFilterEntity.StudentEnrollment],
      todayDate: getTodayDateString(),
    };

    return ApiService.openAi.promptStudentFilters(request).then((response) => {
      const studentFilter = response.studentFilter || {};
      const schoolIds = new Set<number>(studentFilter.schoolIds || []);

      studentResource.districts
        ?.filter((district) => studentFilter.districtIds?.includes(district.id))
        .flatMap((district) => district.schools || [])
        .forEach((school) => schoolIds.add(school.id));

      studentResource.regions
        ?.filter((region) => studentFilter.regionIds?.includes(region.id))
        .flatMap((region) => region.districts || [])
        .flatMap((district) => district.schools || [])
        .forEach((school) => schoolIds.add(school.id));

      props.setFilter({
        schoolYears: studentFilter.schoolYears,
        gradeLevelIds: studentFilter.gradeLevelIds,
        schoolIds: schoolIds.size > 0 ? Array.from(schoolIds) : undefined,
      });
      setValidationErrors({});
      setCompletedAiSearchPrompt(prompt);
    }).catch((ex) => {
      dispatch(setAppToast(newToast("error")));
      setValidationErrors(ex.data);
    });
  };

  const handleFavoriteToast = useCallback((message: string) => {
    const type = message.includes("unavailable") ? "error" : "success";
    dispatch(setAppToast(newToast(type, message)));
  }, [dispatch]);

  const loadFavoriteSearches = useCallback(() => {
    return ApiService.aiSearchFavorites.getList(aiSearchContext);
  }, []);

  const saveFavoriteSearch = useCallback((favorite) => {
    return ApiService.aiSearchFavorites.saveCreate({
      id: typeof favorite.id === "number" ? favorite.id : undefined,
      name: favorite.name,
      query: favorite.query,
      searchContext: aiSearchContext,
      isFavorite: favorite.isFavorite,
      displayOrder: favorite.displayOrder ?? favorite.order,
    });
  }, []);

  const updateFavoriteSearch = useCallback((favorite) => {
    return ApiService.aiSearchFavorites.saveUpdate({
      id: typeof favorite.id === "number" ? favorite.id : undefined,
      name: favorite.name,
      query: favorite.query,
      searchContext: aiSearchContext,
      isFavorite: favorite.isFavorite,
      displayOrder: favorite.displayOrder ?? favorite.order,
    });
  }, []);

  const deleteFavoriteSearch = useCallback((id) => {
    return ApiService.aiSearchFavorites.delete(Number(id));
  }, []);

  const markFavoriteSearchUsed = useCallback((id) => {
    return ApiService.aiSearchFavorites.markUsed(Number(id));
  }, []);

  const saveFavoriteSearchOrder = useCallback((favorites) => {
    return ApiService.aiSearchFavorites.saveOrder(
      favorites
        .filter((favorite) => typeof favorite.id === "number")
        .map((favorite) => ({ id: favorite.id as number, displayOrder: favorite.displayOrder }))
    );
  }, []);

  const schoolYearField = () => {
    const propertyName = pn<ControlGroupStudentFilterModel>("schoolYears");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.schoolYearsWithActive?.filter((x) => props.filter.schoolYears?.includes(x.id))}
        options={resource.schoolYearsWithActive}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolYears = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const schoolsField = () => {
    const propertyName = pn<ControlGroupStudentFilterModel>("schoolIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.schools?.filter((x) => props.filter.schoolIds?.includes(x.id))}
        options={resource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const gradeLevelsField = () => {
    const propertyName = pn<ControlGroupStudentFilterModel>("gradeLevelIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.gradeLevels?.filter((x) => props.filter.gradeLevelIds?.includes(x.id))}
        options={resource.gradeLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.gradeLevelIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  return (
    <Stack ref={breakpointsRef} spacing="lg">
      {props.setCustomFilters && (
        <CustomFilters
          allowedEntityTypes={entityOptions}
          filters={props.customFilters}
          setFilters={props.setCustomFilters}
          properties={resource.customFilterProperties}
        />
      )}

      {((appUserRoleId == LkRoleId.Cobro || appUserRoleId == LkRoleId.Administrator) && studentResource.canAccessAiFeature) && (
        <Box>
          <AISearchHeader
            value={aiSearchPrompt}
            onChange={setAiSearchPrompt}
            onSubmit={submitAiFilter}
            validationErrors={validationErrors}
            showFavorites
            userId={appUserId}
            completedSearch={completedAiSearchPrompt}
            onFavoriteToast={handleFavoriteToast}
            loadFavoriteSearches={loadFavoriteSearches}
            createFavoriteSearch={saveFavoriteSearch}
            updateFavoriteSearch={updateFavoriteSearch}
            deleteFavoriteSearch={deleteFavoriteSearch}
            markFavoriteSearchUsed={markFavoriteSearchUsed}
            saveFavoriteSearchOrder={saveFavoriteSearchOrder}
          />
        </Box>
      )}
      <Grid container spacing="sm">
        <Grid item xs={largeFieldBreakpoints}>
          {schoolYearField()}
        </Grid>

        <Grid item xs={largeFieldBreakpoints}>
          {gradeLevelsField()}
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          {schoolsField()}
        </Grid>
      </Grid>
    </Stack>
  );
};
